import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { API, pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Empty, Spinner } from "@/components/common";
import { toast } from "sonner";
import { FileText, Loader2, Download, Eye, CheckCircle2, Sparkles, FileDown, Zap } from "lucide-react";
import DocEditor from "@/components/DocEditor";

export default function GenerateStep({ meeting, project, refresh, pid, mid }) {
  const [selected, setSelected] = useState(["MOM", "BRD", "FSD"]);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [fpBusy, setFpBusy] = useState(false);
  const [fpStep, setFpStep] = useState("");
  const queryClient = useQueryClient();

  const { data: catalog } = useQuery({ queryKey: ["catalog"], queryFn: async () => (await api.get("/catalog")).data });
  const { data: docs, refetch } = useQuery({ queryKey: ["documents", pid], queryFn: async () => (await api.get(`/projects/${pid}/documents`)).data });

  const docTypes = catalog?.doc_types || {};
  const toggle = (k) => setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const generate = async () => {
    if (!meeting.analysis) { toast.error("Run the Clarify analysis first"); return; }
    if (!selected.length) { toast.error("Select at least one document"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/projects/${pid}/documents/generate`, { meeting_id: mid, types: selected }, { timeout: 60000 });
      const result = await pollJob(res.data.job_id);
      toast.success(`Generated ${result?.created?.length || 0} document(s)`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Generation failed"); } finally { setBusy(false); }
  };

  const runFullPipeline = async () => {
    if (!selected.length) { toast.error("Select at least one document first"); return; }
    setFpBusy(true); setFpStep("Starting…");
    try {
      const res = await api.post(`/projects/${pid}/meetings/${mid}/full-pipeline`, { types: selected }, { timeout: 60000 });
      const r = await pollJob(res.data.job_id, { onTick: (s) => setFpStep(s) });
      toast.success(`Pipeline complete · ${r.documents} doc(s), ${r.tasks} task(s), ${r.interfaces} interface(s) with code`);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["tasks", pid] });
      queryClient.invalidateQueries({ queryKey: ["interfaces", pid] });
      refresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Pipeline failed"); }
    finally { setFpBusy(false); setFpStep(""); }
  };

  const approve = async (d) => {
    await api.post(`/documents/${d.id}/approve`, { version: "1.0" });
    toast.success(`${d.doc_type} approved as v1.0`);
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* One-click full pipeline */}
      <div className="rounded-xl border bg-gradient-to-br from-[#0f2557] to-[#1a3a7a] text-white p-6" data-testid="full-pipeline-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 font-semibold"><Zap size={18} className="text-[#c9a227]" /> One-click pipeline</div>
            <p className="text-sm text-white/70 mt-1">Run the whole chain for this meeting: analyze → generate the selected deliverables → build the project plan → derive interfaces → generate starter code. Uses the documents selected below.</p>
          </div>
          <Button onClick={runFullPipeline} disabled={fpBusy} className="gap-2 bg-[#c9a227] hover:bg-[#b5901f] text-[#0f2557] font-semibold shrink-0" data-testid="run-full-pipeline">
            {fpBusy ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />} {fpBusy ? "Running…" : "Meeting → all deliverables + code"}
          </Button>
        </div>
        {fpBusy && <div className="mt-3 text-xs text-[#c9a227] flex items-center gap-2" data-testid="full-pipeline-step"><Loader2 className="animate-spin" size={13} /> {fpStep}</div>}
      </div>

      <div className="rounded-xl border bg-card p-6">
        <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3">Select Deliverables</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(docTypes).map(([k, v]) => (
            <button key={k} onClick={() => toggle(k)} data-testid={`doctype-${k}`}
              className={`text-left p-3 rounded-lg border transition ${selected.includes(k) ? "border-[#29abe2] bg-[#29abe2]/5" : "hover:border-[#29abe2]/40"}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#0f2557] dark:text-[#29abe2]">{k}</span>
                {selected.includes(k) && <CheckCircle2 size={14} className="text-[#29abe2]" />}
              </div>
              <div className="text-xs text-muted-foreground mt-1 leading-tight">{v}</div>
            </button>
          ))}
        </div>
        <Button onClick={generate} disabled={busy} className="mt-4 gap-2" data-testid="generate-docs">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} {busy ? "Generating branded documents…" : `Generate ${selected.length} document(s)`}
        </Button>
        {!meeting.analysis && <p className="text-xs text-[#c9a227] mt-2">Tip: run the Clarify analysis first for evidence-backed content.</p>}
      </div>

      <div>
        <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3">Generated Documents</div>
        {!docs ? <Spinner /> : docs.length === 0 ? (
          <Empty title="No documents yet" hint="Select deliverables above and generate a branded, client-ready pack." />
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {docs.map((d) => (
              <div key={d.id} className="rounded-xl border bg-card p-4 card-hover" data-testid={`doc-${d.doc_type}`}>
                <div className="flex items-start gap-3">
                  <div className="grid place-items-center w-10 h-10 rounded-lg bg-[#0f2557]/10 dark:bg-[#29abe2]/15 text-[#0f2557] dark:text-[#29abe2]"><FileText size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#0f2557] dark:text-[#29abe2]">{d.doc_type}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.status === "approved" ? "bg-emerald-500/15 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>{d.status} v{d.version}</span>
                    </div>
                    <div className="font-medium text-sm text-foreground truncate">{d.title}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{d.reference}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setViewing(d)} data-testid={`view-${d.doc_type}`}><Eye size={13} /> Open</Button>
                  <a href={`${API}/documents/${d.id}/export?format=pdf`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="gap-1.5 h-8" data-testid={`pdf-${d.doc_type}`}><FileDown size={13} /> PDF</Button>
                  </a>
                  <a href={`${API}/documents/${d.id}/export?format=docx`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="gap-1.5 h-8" data-testid={`download-${d.doc_type}`}><Download size={13} /> DOCX</Button>
                  </a>
                  {d.status !== "approved" && <Button size="sm" variant="ghost" className="gap-1.5 h-8 text-emerald-600" onClick={() => approve(d)}><CheckCircle2 size={13} /> Approve</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="sr-only"><DialogTitle>Document</DialogTitle></DialogHeader>
          {viewing && <DocEditor docMeta={viewing} onSaved={refetch} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
